import type { CameraSlice } from '../../core/camera';
import {
  coordinates,
  getCoordinateTransformContext,
} from '../../core/coordinate-system';
import {
  type Feature,
  type FeatureConfig,
  insideCanvas,
} from '../../core/feature-system';
import { normalizeWheelDelta } from '../../core/utils';
import { type ZoomSlice, zoomSlice } from './slice';

export type ZoomConfig = FeatureConfig<{
  /**
   * Zoom sensitivity multiplier applied to the wheel delta.
   * Higher values = faster zoom per scroll tick.
   * @default 0.001
   */
  speed?: number;
  /**
   * Minimum allowed zoom level (furthest out).
   * Values below this are clamped — the wheel event is consumed but no zoom
   * change occurs.
   * @default 0.1
   */
  minZoom?: number;
  /**
   * Maximum allowed zoom level (closest in).
   * Values above this are clamped — the wheel event is consumed but no zoom
   * change occurs.
   * @default 10
   */
  maxZoom?: number;
}>;

const DEFAULT_CONFIG: Required<Omit<ZoomConfig, 'shouldRun'>> = {
  speed: 0.001,
  minZoom: 0.1,
  maxZoom: 10,
};

export const createZoomFeature = (config?: ZoomConfig) =>
  ({
    id: 'zoom',
    slice: zoomSlice,
    config,

    createHandler: (handlerConfig?: ZoomConfig) => {
      const cfg = { ...DEFAULT_CONFIG, ...config, ...handlerConfig };

      return {
        id: 'zoom',
        shouldRun: (event, ctx) => {
          if (
            event.type !== 'wheel' ||
            !event.rawEvent.metaKey ||
            !insideCanvas(event)
          ) {
            return false;
          }
          return cfg.shouldRun ? cfg.shouldRun(event, ctx) : true;
        },
        handleEvent: (event, ctx) => {
          if (event.type !== 'wheel') {
            return undefined;
          }

          const state = ctx.store.getState();
          const { deltaY } = normalizeWheelDelta(event.rawEvent);

          const { speed, minZoom, maxZoom } = state.zoom ?? cfg;

          const delta = -deltaY * speed;
          const newZoom = state.canvas.transform.zoom * (1 + delta);
          if (newZoom < minZoom || newZoom > maxZoom) {
            return { stopPropagation: true };
          }

          const transformCtx = getCoordinateTransformContext(state.canvas);
          const origin = coordinates.point.screenToViewport(transformCtx)({
            x: event.clientX,
            y: event.clientY,
          });

          state.zoom.actions.zoomBy(delta, origin);

          return { stopPropagation: true, startLock: true };
        },
      };
    },
  }) satisfies Feature<CameraSlice, ZoomSlice, ZoomConfig>;
